<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { DateTime } from 'luxon'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, resolve } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'
    import { type as typeOf } from 'hydria/utils/type'

    import { Breadcrumb } from 'breadcrumb'

    // import type { IDomain } from 'wdom/domain/interfaces'

    import type { DomainHandler } from './domain'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const domainIdna = getContext('domainIdna') as Readable<string>
    const Domain = getContext('Domain') as DomainHandler

    let ready = false
    let loading = $state(false)
    let domainLog: Array<any> = $state([])
    let nItems = 0

    let diffLoading = $state(false)
    let revisionAId: string | undefined = $state()
    let revisionBId: string | undefined = $state()
    let revisionA: any = $state()
    let revisionB: any = $state()
    let removedFields: Set<string> = $state(new Set())
    let changedFields: any = $state()
    let revisionDocumentKeys: string[] = $state([])
    let showAll = $state(false)

    const alwaysShown = new Set(['cira_status', 'contacts', 'epp_auth_password', 'expiration', 'last_updated', 'locked', 'nameservers', 'stage_of_life', 'stage_of_life_end', 'status'])

    let status = $state()
    unsubs.push(
        Domain.status.subscribe(($status) => {
            status = $status
            if (status == 'ready') loadLog()
            else domainLog = []
        }),
    )

    // let domain: IDomain = $derived(status == 'ready' ? Domain.domain : ({} as IDomain))

    async function loadLog() {
        loading = true
        const res = await Domain.loadLog(0, undefined)

        if (!res) {
            console.warn('loadLog returned nothin')
            loading = false
            return
        }
        domainLog = res.entries
        nItems = res.total
        if (domainLog.length >= 2) {
            revisionAId = domainLog[1].revision
            revisionBId = domainLog[0].revision
            loadDiff(revisionAId as string, revisionBId as string)
        }
        loading = false
        if (!ready) ready = true
    }

    async function loadDiff(revA: string, revB: string) {
        diffLoading = true
        let r: any
        try {
            r = await simpleFetch('GET', `/json/domain/1/${$domainIdna}/revision-diff/${revA}/${revB}`)
            for (const rev of [r.data.revision_a, r.data.revision_b]) {
                // if (rev.stamp) rev.stamp = toLuxon(rev.stamp)

                for (const key of Object.keys(rev)) {
                    if (rev[key] && (rev[key].__type == 'datetime' || (rev[key].match && rev[key].match(/^\d{4}-\d{2}-\d{2}/)))) {
                        rev[key] = toLuxon(rev[key])
                    }
                }
            }

            revisionA = r.data.revision_a
            revisionB = r.data.revision_b

            if (r.data.removed_fields && r.data.removed_fields.length) {
                removedFields = new Set(r.data.removed_fields)
            } else {
                removedFields = new Set()
            }

            let keys: any = new Set(Object.keys(revisionA).concat(Object.keys(revisionB)))
            keys = [...keys]
            keys.sort()
            revisionDocumentKeys = keys

            changedFields = r.data.changed_fields
        } catch (e) {
            console.error('Failed to load diff:', e)
            notify({
                type: 'danger',
                title: 'Diff',
                text: 'La différence entre les deux revision a généré une erreur.',
            })
        }

        diffLoading = false
    }

    function setRevA(id: string) {
        let ai: number
        let bi: number
        for (const [i, entry] of domainLog.entries()) {
            if (entry.revision == id) ai = i
            if (entry.revision == revisionBId) bi = i
        }
        // @ts-expect-error
        if (ai == undefined || bi == undefined) {
            revisionAId = undefined
            return
        }
        if (ai <= bi) bi = ai - 1

        revisionAId = domainLog[ai].revision
        revisionBId = domainLog[bi].revision
        loadDiff(revisionAId as string, revisionBId as string)
    }
    function setRevB(id: string) {
        let ai: number
        let bi: number
        for (const [i, entry] of domainLog.entries()) {
            if (entry.revision == revisionAId) ai = i
            if (entry.revision == id) bi = i
        }
        // @ts-expect-error
        if (ai == undefined || bi == undefined) {
            revisionBId = undefined
            return
        }
        if (ai <= bi) ai = bi + 1

        revisionAId = domainLog[ai].revision
        revisionBId = domainLog[bi].revision
        loadDiff(revisionAId as string, revisionBId as string)
    }

    function cleanField(k: string, v: any) {
        const ftype = typeOf(v)
        if (v == null) v = 'null'
        else if (k == 'contacts') {
            v = v.map((contact: any) => `${contact.type}:${contact.id}`).join(', ')
            // } else if (v instanceof DateTime) {
            //     v = v.toFormat('yyyy-MM-dd HH:mm:ss')
        } else if (ftype == 'boolean') {
            v = v ? 'true' : 'false'
        } else if (ftype == 'array') {
            v = v.join(', ')
        }
        return v
    }

    let crumbs = $derived([
        { txt: $_('Domains'), url: spath('/domains') },
        {
            txt: status == 'ready' ? Domain.unicode : status == 'loading' ? '…' : iconExclamationTriangle,
            html: status != 'ready' && status != 'loading',
            url: status == 'ready' ? spath(`/domain/${$domainIdna}`) : undefined,
        },
        { txt: $_('Revisions') },
    ])
</script>

<Breadcrumb {crumbs} />

{#if status == 'not-found' || status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else if status == 'loading'}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Modification log')}</div>
        <div class="card-body">
            <div class="table-responsive" style="overflow-y: scroll; max-height: 250px;">
                <table class="table table-striped fixed-no-overflow table-condensed">
                    <thead>
                        <tr>
                            <th>{$_('Before')}</th>
                            <th>{$_('After')}</th>
                            <th>{$_('Date')}</th>
                            <th>{$_('Modification')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#if loading}
                            <tr>
                                <td colspan="4"><LoadingPattern pattern={['x', 'x', 'x', 'x']} /></td>
                            </tr>
                        {:else}
                            {#each domainLog as entry, i}
                                <tr class:warning={revisionA && revisionB && domainLog[i].revision >= revisionA.revision && domainLog[i].revision <= revisionB.revision}>
                                    <td onclick={() => setRevA(domainLog[i].revision)} aria-hidden="true"
                                        >{#if i}<Icon icon={domainLog[i].revision == revisionAId ? 'fa:dot-circle-o' : 'fa:circle-o'} />{/if}</td
                                    >
                                    <td onclick={() => setRevB(domainLog[i].revision)} aria-hidden="true"
                                        >{#if i < domainLog.length - 1}<Icon icon={domainLog[i].revision == revisionBId ? 'fa:dot-circle-o' : 'fa:circle-o'} />{/if}</td
                                    >
                                    <td><Date value={domainLog[i].stamp} format="h-table-s" /></td>
                                    <td>{$resolve(domainLog[i].comment_i18n)} :: i={i}, dl[i].revision={domainLog[i].revision}; aid={revisionAId}; bid={revisionBId}</td>
                                </tr>
                            {/each}
                        {/if}
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    {#if revisionAId && revisionBId}
        <div class="card mt-4">
            <div class="card-title text-primary text-end">
                {#if !diffLoading}
                    <button type="button" class="btn btn-sm btn-outline-secondary" class:active={showAll} onclick={() => (showAll = !showAll)}>{$_('Show all')}</button>
                {/if}
                {$_('Revision comparison')}
            </div>
            <div class="card-body">
                <table class="table fixed-no-overflow table-condensed">
                    <thead>
                        <tr>
                            <th style="width: 20%">Field</th>
                            <th style="width: 40%">Before</th>
                            <th style="width: 40%">After</th>
                        </tr>
                    </thead>
                    {#if diffLoading || revisionA == null || revisionB == null}
                        <tbody
                            ><tr
                                ><td colspan="3">
                                    <LoadingPattern pattern={['x', 'x', 'x', 'x']} />
                                </td></tr
                            ></tbody
                        >
                    {:else}
                        <tbody>
                            <tr class="warning">
                                <td>Action</td>
                                <td>{revisionA.action}</td>
                                <td>{revisionB.action}</td>
                            </tr>
                            <tr class="warning">
                                <td>Revision</td>
                                <td>{revisionA.revision}</td>
                                <td>{revisionB.revision}</td>
                            </tr>
                            <tr class="warning">
                                <td>Date</td>
                                <td><Date value={revisionA.stamp} format="h-table-s" /></td>
                                <td><Date value={revisionB.stamp} format="h-table-s" /></td>
                            </tr>
                            {#each revisionDocumentKeys as key}
                                {#if showAll || alwaysShown.has(key)}
                                    <tr>
                                        <td class:text-danger={removedFields.has(key)} class:text-warning={changedFields && changedFields[key]}>{key}</td>
                                        <td>
                                            {#if revisionA[key] !== undefined}
                                                <span>
                                                    {#if revisionA[key] instanceof DateTime}<Date value={revisionA[key]} format="table-s" />
                                                    {:else if key == 'ds_records'}{#each revisionA[key] as line, linei}
                                                            Entry {linei + 1}:<br />
                                                            {#each Object.entries(line) as [lk, lv]}
                                                                {lk}: {lv}<br />
                                                            {/each}
                                                            <br />{/each}
                                                    {:else if typeOf(revisionA[key]) === 'object'}{#each Object.entries(revisionA[key]) as ok, ov}{ok}={ov}<br />{/each}
                                                    {:else}{cleanField(key, revisionA[key])}{/if}
                                                </span>
                                            {/if}
                                        </td>
                                        <td>
                                            {#if revisionB[key] !== undefined}
                                                <span>
                                                    {#if revisionB[key] instanceof DateTime}<Date value={revisionB[key]} format="table-s" />
                                                    {:else if key == 'ds_records'}{#each revisionB[key] as line, linei}
                                                            Entry {linei + 1}:<br />
                                                            {#each Object.entries(line) as [lk, lv]}
                                                                {lk}: {lv}<br />
                                                            {/each}
                                                            <br />{/each}
                                                    {:else if typeOf(revisionB[key]) === 'object'}{#each revisionB[key] as ok, ov}{ok}={ov}<br />{/each}
                                                    {:else}{cleanField(key, revisionB[key])}{/if}
                                                </span>
                                            {/if}
                                        </td>
                                    </tr>
                                {/if}
                            {/each}
                        </tbody>
                    {/if}
                </table>
            </div>
        </div>
    {/if}
{/if}
